import { cn } from '@mds/shared/lib/utils';

/**
 * A headline figure with a label above and a note below.
 *
 * `density` and `value` carry the two shells this app already used: the pages
 * built on full-width cards (`comfortable`) and the tighter KPI strips
 * (`compact`). The classes each combination produces are exactly the ones the
 * four hand-written copies of this card produced before they were merged.
 */
type StatDensity = 'comfortable' | 'compact';
type StatValueSize = 'sm' | 'md' | 'lg' | 'hero';

const shells: Record<StatDensity, string> = {
  comfortable: 'border border-hairline p-4 sm:p-6',
  compact: 'border border-hairline p-4 sm:p-5',
};

const labels: Record<StatDensity, string> = {
  comfortable: 'tracking-wide text-ink-secondary',
  compact: 'tracking-wider text-ink-muted',
};

// Each size names its paper size too: a quarter of A4 is narrower than any
// screen column, and a figure that overflows its tile is a figure nobody reads.
const values: Record<StatValueSize, string> = {
  sm: 'text-xl leading-tight font-semibold tracking-tight print:text-lg',
  md: 'text-3xl leading-none sm:text-4xl font-semibold tracking-tighter print:text-2xl',
  lg: 'text-3xl leading-none sm:text-4xl font-semibold tracking-tighter print:text-2xl',
  hero: 'text-4xl leading-none sm:text-5xl font-semibold tracking-tighter print:text-2xl',
};

/**
 * Renders one figure. See the variant note above for what each shell is.
 *
 * `tag` marks a figure that is not yet what it will be — counted in the
 * browser, or read off a fixture. A number nobody can tell is provisional is
 * worse than no number, so it is said on the tile rather than in a footnote.
 */
export default function StatCard({
  label,
  value,
  note,
  tag,
  density = 'comfortable',
  size = 'md',
  onClick,
  actionLabel,
}: {
  label: string;
  value: string;
  note: string;
  tag?: string;
  density?: StatDensity;
  size?: StatValueSize;
  /** Makes the whole tile a button — for a figure that can be broken down. */
  onClick?: () => void;
  /** What the button announces, when the label alone would not say it. */
  actionLabel?: string;
}) {
  const shell = cn(
    'flex min-w-0 flex-col gap-1.5 rounded-2xl bg-surface shadow-card print:gap-1 print:p-4',
    shells[density],
  );

  const body = (
    <>
      <span className="flex flex-wrap items-center gap-2">
        <span className={cn('font-mono text-sm uppercase', labels[density])}>{label}</span>
        {tag && (
          <span className="rounded-md border border-warn-edge bg-warn-wash px-1.5 py-0.5 font-mono text-sm tracking-wide text-warn-text uppercase">
            {tag}
          </span>
        )}
      </span>
      {/* Proportional figures on display-size values; tabular only in columns. */}
      <span className={cn('break-words', values[size])}>{value}</span>
      <span className="text-base text-ink-muted">{note}</span>
    </>
  );

  if (!onClick) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={actionLabel}
      className={cn(
        shell,
        'cursor-pointer text-left transition-transform duration-(--duration-fast) ease-standard',
        'hover:-translate-y-0.5 hover:border-brand-edge',
        'focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
      )}
    >
      {body}
    </button>
  );
}
